import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  Feature,
  ListingClient,
  ListingQuery,
  RetailSaleListingTickets,
  SectionalListingsForEventsInput,
} from 'src/WebApiController';

/**
 * Loads RetailSaleListingTickets (tickets + the small set of listing fields
 * the checkout table needs) for a ListingQuery filter. Used by the
 * multi-listing retail-sale checkout (POS-8649) to display ticket rows
 * grouped by listing.
 *
 * Uses `GetRetailSaleListingTicketsForEvents` to batch-fetch all matching
 * listings (with tickets) in a single API call instead of N parallel
 * per-ID requests.
 * Both entity-ID filters (fast path: individual listings selected) and
 * event-ID filters (slow path: event-only selection) are supported —
 * the backend handles both via the same endpoint.
 *
 * Feature-gated on Feature.SalesGroupView — only sellers in the
 * sales-group pilot should issue these detail loads.
 */
export const useRetailSaleListingTickets = (
  filterQuery: ListingQuery | null
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasFeature = useUserHasFeature(Feature.SalesGroupView);

  const entityIds = filterQuery?.entityIds ?? null;
  const eventOrMappingIds = filterQuery?.eventOrMappingIds ?? null;

  const hasIds =
    (entityIds != null && entityIds.length > 0) ||
    (eventOrMappingIds != null && eventOrMappingIds.length > 0);

  const shouldQuery =
    hasFeature &&
    activeAccountWebClientConfig.activeAccountId != null &&
    filterQuery != null &&
    hasIds;

  // Stable cache keys — sort so selection order doesn't bust the cache
  const sortedEntityIds = useMemo(
    () => (entityIds ?? []).toSorted((a, b) => a - b),
    [entityIds]
  );
  const sortedEventIds = useMemo(
    () => (eventOrMappingIds ?? []).toSorted(),
    [eventOrMappingIds]
  );

  return useQuery({
    queryKey: [
      'retailSaleListingTickets',
      activeAccountWebClientConfig.activeAccountId,
      sortedEntityIds,
      sortedEventIds,
    ],
    queryFn: async (): Promise<RetailSaleListingTickets[]> => {
      if (!shouldQuery || !filterQuery) return [];

      const client = new ListingClient(activeAccountWebClientConfig);
      const input: SectionalListingsForEventsInput = {
        listingQuery: filterQuery,
        curListings: null,
      };
      const result = await client.getRetailSaleListingTicketsForEvents(input);
      return result ?? [];
    },
    enabled: shouldQuery,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError(
          'ListingClient.getRetailSaleListingTicketsForEvents',
          error,
          {
            entityIds,
            eventOrMappingIds,
          }
        );
      },
    },
  });
};
